/**
 * Top navigation: the filter tabs (All · Days · saved views · +, scrolling) and, set apart
 * on the right, ✓ Done; find sits in the row below (TabToolbar). Tabs are ways of looking at the
 * same items; drop any card or row on the bar to pin it as a tab. Drag a saved tab sideways
 * to reorder (on phones: press, then slide). Press and hold still (or right-click) to edit it.
 */
import { useEffect, useRef, useSyncExternalStore, type PointerEvent as RPointerEvent, type ReactNode } from 'react'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { navigate, openSheet, useLocation, type Route } from '../lib/router'
import { useItem, useMySettings } from '../store/hooks'
import { store } from '../store/store'
import { cancelActiveDrag, useTreeDnd } from '../dnd/TreeDnd'
import { viewItemTitle } from '../lib/viewTitle'
import { useSavedViews, SyncDot } from './AppShell'
import { IconCheck, IconDays, IconPlus } from './Icons'
import { haptic } from '../lib/haptics'
import { guardHoldRelease } from '../lib/holdRelease'

export { viewItemTitle, viewTitle } from '../lib/viewTitle'

export const BUILTIN_TABS = [
  { key: 'board', label: 'All', path: '/home' },
  { key: 'days', label: 'Days', path: '/days' },
] as const

const TAB_ROUTES = new Set(['home', 'board', 'days', 'done', 'view'])
export function isTabRoute(route: Route): boolean {
  return TAB_ROUTES.has(route.name)
}

/** The active tab key for a route ('board', 'days', …, or `view:<id>`). */
export function useActiveTab(): string {
  const { route } = useLocation()
  const home = String(useMySettings().homeView || 'board')
  if (route.name === 'home') return home
  if (route.name === 'view') return `view:${route.id}`
  return route.name
}

export function TabBar({ right, sub }: { right?: ReactNode; sub?: ReactNode }) {
  const active = useActiveTab()
  const home = String(useMySettings().homeView || 'board')
  const views = useSavedViews()
  const dnd = useTreeDnd()
  // A card or row being dragged (to pin or add to a tab) — not a tab being reordered, nor a chart.
  const dragging = !!dnd?.state.activeId && dnd.state.kind !== 'tab' && dnd.state.kind !== 'chart'
  const { setNodeRef, isOver } = useDroppable({ id: 'zone:tabs', data: { type: 'zone' } })
  const navRef = useRef<HTMLElement>(null)

  // Cards' bars stick right under this header (SPEC §7.10): publish its height.
  useEffect(() => {
    const el = navRef.current?.closest('[data-tab-header]') as HTMLElement | null
    if (!el) return
    const set = () => document.documentElement.style.setProperty('--tabs-h', `${el.offsetHeight}px`)
    set()
    const ro = new ResizeObserver(set)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // Keep the active tab visible when the bar scrolls sideways on phones.
  useEffect(() => {
    const el = navRef.current?.querySelector('[aria-current="page"]') as HTMLElement | null
    el?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' })
  }, [active])

  return (
    <header className="sticky top-0 z-20 bg-bg" style={{ paddingTop: 'var(--safe-t)' }} data-tab-header>
      <div ref={setNodeRef} className={`flex h-11 items-stretch gap-3 pr-2 pl-3.5 transition-colors md:pl-5 ${isOver ? 'bg-surface-2' : ''}`}>
        <nav ref={navRef} className="no-scrollbar flex min-w-0 flex-1 items-stretch gap-4 overflow-x-auto" aria-label="Views">
          {BUILTIN_TABS.map((t) => (
            <Tab
              key={t.key}
              tabKey={t.key}
              home={home === t.key}
              active={active === t.key}
              onClick={() => navigate(t.path)}
              onHold={t.key === 'days' ? () => openSheet('days') : undefined}
              title={t.key === 'days' ? t.label : undefined}
            >
              {t.key === 'days' ? (
                // Days is just its calendar mark, to set it apart from the tabs you make.
                <span className="flex items-center">
                  <IconDays size={17} />
                  <span className="sr-only">{t.label}</span>
                </span>
              ) : (
                t.label
              )}
            </Tab>
          ))}
          {views.map((id) => (
            <ViewTab key={id} id={id} home={home === `view:${id}`} active={active === `view:${id}`} />
          ))}
          <button
            type="button"
            onClick={() => openSheet('view', {})}
            className={`flex h-8 flex-none items-center justify-center gap-1 self-center rounded-sm text-sm transition-all ${
              dragging ? `border border-dashed px-3 ${isOver ? 'border-fg text-fg' : 'border-line-2 text-muted'}` : 'w-8 text-muted hover:bg-surface-2 hover:text-fg'
            }`}
            aria-label="New view"
            title="New view — or drag a card here to pin it as a tab"
          >
            <IconPlus size={16} />
            {dragging && <span className="whitespace-nowrap">Pin as a tab</span>}
          </button>
        </nav>
        <DoneTab active={active === 'done'} />
        <span className="flex flex-none items-center gap-1">
          <span className="md:hidden">
            <SyncDot />
          </span>
          {right}
        </span>
      </div>
      {sub}
    </header>
  )
}

/** ✓ Done; while a chart is being dragged, it can be dropped here too (it moves to Done). */
function DoneTab({ active }: { active: boolean }) {
  const st = useTreeDnd()?.state
  const chart = st?.kind === 'chart' && !!st.activeId && !st.activeId.startsWith('chart:done:')
  const drop = useDroppable({ id: 'zone:tab:done', data: { type: 'zone' }, disabled: !chart })
  return (
    <span ref={drop.setNodeRef} className={`flex flex-none rounded-sm ${drop.isOver ? 'ring-2 ring-fg' : chart ? 'ring-1 ring-line-2' : ''}`}>
      <Tab tabKey="done" active={active || drop.isOver} onClick={() => navigate('/done')}>
        <span className="flex items-center gap-1">
          <IconCheck size={13} />
          Done
        </span>
      </Tab>
    </span>
  )
}

/** `tabKey` (and `home`, for the one Home opens on) tell a screen move which way to slide. */
function Tab({
  tabKey,
  home,
  active,
  onClick,
  onHold,
  title,
  children,
}: {
  tabKey: string
  home?: boolean
  active: boolean
  onClick: () => void
  onHold?: () => void
  title?: string
  children: ReactNode
}) {
  const hold = useHold(onHold)
  return (
    <button
      type="button"
      onClick={() => !hold.consumed() && onClick()}
      {...hold.handlers}
      aria-current={active ? 'page' : undefined}
      title={title}
      data-tab-key={tabKey}
      data-tab-home={home ? '' : undefined}
      // Text with a line under the one you're on (SPEC §7.10); the line glides to the next one.
      className={`relative flex max-w-44 flex-none items-center truncate text-[0.875rem] ${active ? 'font-medium text-fg' : 'text-muted hover:text-fg'}`}
    >
      {children}
      {active && <span className="tab-line absolute inset-x-0 bottom-0 h-[1.5px] bg-fg" aria-hidden="true" />}
    </button>
  )
}

/**
 * A saved tab. Unnamed tabs follow the cards they're inside. It's also a drop target:
 * drop a card or row on it to add that to the tab's "Inside".
 */
function ViewTab({ id, home, active }: { id: string; home: boolean; active: boolean }) {
  const title = useViewTitle(id)
  const st = useTreeDnd()?.state
  const dragging = !!st?.activeId
  const reordering = st?.kind === 'tab'
  const drop = useDroppable({ id: `zone:tab:${id}`, data: { type: 'zone' }, disabled: !dragging })
  const drag = useDraggable({ id: `tab:${id}`, data: { type: 'tab', id } })
  const adding = drop.isOver && !reordering
  const self = reordering && st?.activeId === id
  // Where the dragged tab would land: a gap opens before or after this one (inside its own
  // drop area, so it doesn't slide out from under the pointer), with a slot outline.
  const mark = reordering && drop.isOver && !self ? st?.pageDrop : null
  return (
    <span
      ref={(el) => {
        drop.setNodeRef(el)
        drag.setNodeRef(el)
      }}
      {...drag.listeners}
      data-tab={id}
      className={`relative flex flex-none rounded-sm transition-[box-shadow,opacity,padding] duration-150 ${adding ? 'ring-2 ring-fg' : dragging && !reordering ? 'ring-1 ring-line-2' : ''} ${
        self ? 'opacity-30' : ''
      } ${mark === 'before' ? 'pl-12' : mark === 'after' ? 'pr-12' : ''}`}
    >
      {mark && <span className={`absolute inset-y-0 w-11 rounded-sm border-2 border-dashed border-fg/50 ${mark === 'before' ? 'left-0' : 'right-0'}`} aria-hidden="true" />}
      <Tab
        tabKey={`view:${id}`}
        home={home}
        active={active || adding}
        onClick={() => navigate(`/v/${id}`)}
        onHold={() => {
          // Held still: edit instead (on phones the press has already lifted it for a drag).
          cancelActiveDrag()
          openSheet('view', { id })
        }}
      >
        {adding ? `+ ${title}` : title}
      </Tab>
    </span>
  )
}

/** A tab's live name (re-renders only when the name itself changes). */
export function useViewTitle(id: string): string {
  return useSyncExternalStore(store.subscribeContent, () => viewItemTitle(id))
}

/**
 * Press and hold still (0.5 s with a mouse, 0.7 s with a finger) or right-click to run
 * `onHold` instead of the tap. Moving more than a few px (scrolling the strip, or dragging
 * the tab — which on phones lifts at 0.25 s) cancels it; the finger's longer wait leaves time
 * to start sliding after the lift.
 */
function useHold(onHold?: () => void) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const fired = useRef(false)
  const cancel = () => {
    if (timer.current) clearTimeout(timer.current)
    timer.current = null
    start.current = null
  }
  const fire = (stillDown: boolean) => {
    cancel()
    // Android sends contextmenu on long-press too: open the editor once.
    if (fired.current) return
    fired.current = true
    haptic(12)
    // It opens under the finger: letting go mustn't tap the sheet's backdrop and close it.
    if (stillDown) guardHoldRelease()
    onHold?.()
  }
  return {
    consumed: () => {
      const f = fired.current
      fired.current = false
      return f
    },
    handlers: onHold
      ? {
          onPointerDown: (e: RPointerEvent) => {
            fired.current = false
            if (e.button !== 0) return
            start.current = { x: e.clientX, y: e.clientY }
            timer.current = setTimeout(() => fire(true), e.pointerType === 'mouse' ? 500 : 700)
          },
          onPointerMove: (e: RPointerEvent) => {
            const s = start.current
            if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > 8) cancel()
          },
          onPointerUp: cancel,
          onPointerLeave: cancel,
          onPointerCancel: cancel,
          onContextMenu: (e: React.MouseEvent) => {
            e.preventDefault()
            // A right-click, or Android's long-press (a press still in progress).
            fire(!!start.current)
          },
          style: { WebkitTouchCallout: 'none', userSelect: 'none' } as React.CSSProperties,
        }
      : {},
  }
}
